'use client'

import Link from 'next/link'
import clsx from 'clsx'
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder/ImageWithPlaceholder'
import type { LinkField, SbBaseBlockProps, StoryblokAsset } from '@/services/storyblok/storyblok'
import { getImgSrc, getLinkFieldURL } from '@/services/storyblok/Storyblok.helpers'
import { storyblokEditable } from '@storyblok/react'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import { Badge, type BadgeVariant } from 'ui/src/components/Badge/Badge'
import { Button } from 'ui/src/components/Button/Button'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { breakpoints } from 'ui/src/theme/media'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { cardRoot, ctaButton, image, imageAspectRatio, imageWrapper, mainLink } from './ContentGridItemBlock.css'

type AspectRatio = '1 / 1' | '3 / 2' | '4 / 3' | '5 / 4' | '2 / 3' | '3 / 4' | '4 / 5'

export type ContentGridItemBlockProps = SbBaseBlockProps<{
  title: string
  subtitle: string
  image: StoryblokAsset
  link: LinkField
  aspectRatio?: AspectRatio
  priority?: boolean
  badgeLabel?: string
  badgeColor?: BadgeVariant
  buttonLabel?: string
}>

/*
SEO and a11y notes
- Whole card is clickable via an absolutely positioned overlay on the title link
- The optional button is visual-only
 */
export function ContentGridItemBlock({ blok }: ContentGridItemBlockProps) {
  const url = getLinkFieldURL(blok.link, blok.title)
  const hasButton = Boolean(blok.buttonLabel)
  const aspectRatio = blok.aspectRatio ?? '1 / 1'

  if (!url) {
    console.warn('[ContentGridItemBlock]: provided "link" could not be resolved. Skipping ContentGridItemBlock render!')
    return null
  }

  // Hint for responsive srcset selection (does not affect layout). Matches ContentGridBlock's
  // auto-fit grid: one column below sm, typically two above. With only 2 items, wide viewports stay 2-up.
  const imageSizes = `(max-width: ${breakpoints.sm}px) 100vw, 50vw`

  return (
    <div className={clsx(cardRoot, yStack({ gap: 'md', position: 'relative' }))} {...storyblokEditable(blok)}>
      <div className={imageWrapper} style={assignInlineVars({ [imageAspectRatio]: aspectRatio })}>
        <ImageWithPlaceholder
          className={image}
          src={getImgSrc(blok.image.filename)}
          alt={blok.image.alt}
          fill={true}
          sizes={imageSizes}
          imagePriority={blok.priority}
        />
      </div>

      <div className={sprinkles({ paddingInline: 'xs' })}>
        <div className={xStack({ gap: 'xxs', alignItems: 'center', flexWrap: 'wrap' })}>
          <Link className={mainLink} href={url}>
            <Heading as="h3" variant="standard.18" color="textPrimary">
              {blok.title}
            </Heading>
          </Link>
          {blok.badgeLabel && (
            <Badge
              as="span"
              size="small"
              className={sprinkles({ marginLeft: 'xxxs' })}
              variant={blok.badgeColor ?? 'yellow1'}
            >
              {blok.badgeLabel}
            </Badge>
          )}
        </div>

        <Text size="md" color="textSecondary">
          {blok.subtitle}
        </Text>

        {hasButton && (
          <Button className={ctaButton} tabIndex={-1} aria-hidden={true} size="medium" variant="secondary">
            {blok.buttonLabel}
          </Button>
        )}
      </div>
    </div>
  )
}
ContentGridItemBlock.blockName = 'contentGridItem'
